/**
 * CreateSectionModal Component
 *
 * Shared modal for creating new room sections.
 * Used by both AdminPage and SessionDetailsPage.
 *
 * Features:
 * - Two-column layout: Image/Name/Description | Expected Items
 * - Full-width clickable image with fullscreen preview
 * - Expected items table with inline quantity editing
 * - Fuzzy search dropdown for adding items
 * - Optional pre-populated data (image, detected items)
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Save, Layers } from 'lucide-react';
import { RoomSectionCreate, SectionExpectedItemCreate } from '@/types';
import { useCategoryContext } from '@/contexts/CategoryContext';
import { getClassColor, colorWithOpacity } from '@/utils/colorUtils';
import { formatClassNameForDisplay } from '@/utils/classNameUtils';
import ThumbnailPreview from '@/components/ThumbnailPreview';

// Fuzzy matching utilities
function fuzzyScore(query: string, candidate: string): number {
  const q = query.trim().toLowerCase();
  const c = candidate.toLowerCase();
  if (!q) return 0;
  if (c.startsWith(q)) return 1000 - (c.length - q.length);
  const idx = c.indexOf(q);
  if (idx !== -1) return 800 - idx;
  let last = -1;
  let score = 0;
  let streak = 0;
  for (const ch of q) {
    const pos = c.indexOf(ch, last + 1);
    if (pos === -1) return -1;
    streak = pos === last + 1 ? streak + 1 : 1;
    score += 10 * streak;
    if (pos === 0 || /[^a-z0-9]/.test(c[pos - 1])) score += 15;
    last = pos;
  }
  return score;
}

function fuzzyFilter(items: string[], query: string): string[] {
  if (!query.trim()) return items;
  return items
    .map((name) => ({ name, s: fuzzyScore(query, name) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.name);
}

interface CreateSectionModalProps {
  roomId: number;
  availableClasses: string[];
  onClose: () => void;
  onSuccess: (section: any) => void;
  createSection: (data: RoomSectionCreate) => Promise<any>;
  // Optional pre-populated data
  initialImage?: string | null;
  initialExpectedItems?: SectionExpectedItemCreate[];
  title?: string;
}

export const CreateSectionModal: React.FC<CreateSectionModalProps> = ({
  roomId,
  availableClasses,
  onClose,
  onSuccess,
  createSection,
  initialImage = null,
  initialExpectedItems = [],
  title = "Create New Section",
}) => {
  const { getClassCategory, getClassThumbnail } = useCategoryContext();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    expected_items: initialExpectedItems,
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initialImage);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [classDropdownOpen, setClassDropdownOpen] = useState(false);
  const classDropdownRef = React.useRef<HTMLDivElement>(null);
  const classDropdownMenuRef = React.useRef<HTMLDivElement>(null);
  const classDropdownButtonRef = React.useRef<HTMLButtonElement>(null);
  const [classDropdownPos, setClassDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [classQuery, setClassQuery] = useState("");
  const [classHighlight, setClassHighlight] = useState(0);

  // Update imagePreview when initialImage changes (async loading)
  useEffect(() => {
    if (initialImage && !imagePreview) {
      setImagePreview(initialImage);
    }
  }, [initialImage]);

  // Close fullscreen image on any key press
  useEffect(() => {
    if (!showFullscreenImage) return;
    const handler = (e: KeyboardEvent) => setShowFullscreenImage(false);
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showFullscreenImage]);

  useEffect(() => {
    if (!classDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      const path = (e.composedPath && e.composedPath()) || [];
      const insideButton = path.includes(classDropdownRef.current as any);
      const insideMenu = path.includes(classDropdownMenuRef.current as any);
      if (!insideButton && !insideMenu) setClassDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [classDropdownOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Image must be smaller than 5MB");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError("");
    }
  };

  const clearImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageFile(null);
    setImagePreview(null);
  };

  const handleAddExpectedItem = (className: string) => {
    if (formData.expected_items.some((item) => item.class_name === className)) {
      return; // Already added
    }
    setFormData({
      ...formData,
      expected_items: [
        ...formData.expected_items,
        { class_name: className, expected_quantity: 1 },
      ],
    });
    setClassQuery("");
    setClassDropdownOpen(false);
  };

  const handleUpdateQuantity = (className: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    setFormData({
      ...formData,
      expected_items: formData.expected_items.map((item) =>
        item.class_name === className ? { ...item, expected_quantity: newQuantity } : item
      ),
    });
  };

  const handleRemoveExpectedItem = (className: string) => {
    setFormData({
      ...formData,
      expected_items: formData.expected_items.filter((item) => item.class_name !== className),
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.expected_items.length === 0) {
      setError("Please add at least one expected item");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let imagePath: string | undefined = undefined;
      if (imageFile) {
        imagePath = await fileToBase64(imageFile);
      } else if (imagePreview) {
        // Use existing preview (e.g., from analysis image)
        imagePath = imagePreview;
      }

      const sectionData: RoomSectionCreate = {
        room_id: roomId,
        name: formData.name,
        description: formData.description,
        display_order: 0,
        image_path: imagePath,
        expected_items: formData.expected_items,
      };

      const result = await createSection(sectionData);
      onSuccess(result);
    } catch (error: any) {
      console.error("Failed to create section:", error);
      setError(error.response?.data?.detail || "Failed to create section");
    } finally {
      setLoading(false);
    }
  };

  // Filter out already added classes from dropdown
  const availableForSelection = availableClasses.filter(
    (cls) => !formData.expected_items.some((item) => item.class_name === cls)
  );

  return createPortal(
    <>
      {/* Fullscreen Image Preview */}
      {showFullscreenImage && imagePreview && (
        <div
          className="fixed inset-0 z-[200] bg-black bg-opacity-90 flex items-center justify-center cursor-pointer"
          onClick={() => setShowFullscreenImage(false)}
        >
          <img
            src={imagePreview}
            alt="Section preview"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          <div className="absolute top-4 right-4 text-white text-sm opacity-60">
            Click anywhere or press any key to close
          </div>
        </div>
      )}

      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-24 md:pb-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <div
          className="relative z-[101] rounded-lg shadow-xl max-w-4xl w-full max-h-full flex flex-col"
          style={{ backgroundColor: '#ffffff' }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col max-h-[85vh] md:max-h-[90vh]">
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Image, Name, Description */}
                <div className="space-y-4">
                  {/* Image Upload - Full width, clickable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Image
                    </label>
                    {imagePreview ? (
                      <div
                        className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-primary-400 transition-colors"
                        onClick={() => setShowFullscreenImage(true)}
                      >
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={clearImage}
                          className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          Click to enlarge
                        </div>
                      </div>
                    ) : (
                      <label className="w-full aspect-video border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors">
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Upload section image</span>
                        <span className="text-xs text-gray-400 mt-1">Click to browse</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>

                  {/* Section Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Display Case A"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Brief description of this section"
                    />
                  </div>
                </div>

                {/* Right Column: Expected Items */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Items <span className="text-red-500">*</span>
                    </label>

                    {/* Class Dropdown - adds on click */}
                    <div className="relative" ref={classDropdownRef}>
                      <button
                        type="button"
                        ref={classDropdownButtonRef}
                        onClick={() => {
                          if (!classDropdownOpen) {
                            const rect = classDropdownButtonRef.current!.getBoundingClientRect();
                            setClassDropdownPos({
                              top: rect.bottom + window.scrollY + 4,
                              left: rect.left + window.scrollX,
                              width: rect.width,
                            });
                          }
                          setClassDropdownOpen((v) => !v);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-500"
                      >
                        Select item class to add...
                      </button>
                      {classDropdownOpen &&
                        classDropdownPos &&
                        createPortal(
                          <div
                            ref={classDropdownMenuRef}
                            className="fixed border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
                            style={{ top: classDropdownPos.top, left: classDropdownPos.left, width: classDropdownPos.width, backgroundColor: '#ffffff', zIndex: 150 }}
                          >
                            <div className="p-2 border-b sticky top-0" style={{ backgroundColor: '#ffffff' }}>
                              <input
                                autoFocus
                                value={classQuery}
                                onChange={(e) => {
                                  setClassQuery(e.target.value);
                                  setClassHighlight(0);
                                }}
                                onKeyDown={(e) => {
                                  const filtered = fuzzyFilter(availableForSelection, classQuery);
                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setClassHighlight((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
                                  }
                                  if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setClassHighlight((h) => Math.max(h - 1, 0));
                                  }
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const pick = filtered[classHighlight] || filtered[0];
                                    if (pick) {
                                      handleAddExpectedItem(pick);
                                    }
                                  }
                                  if (e.key === "Escape") {
                                    setClassDropdownOpen(false);
                                  }
                                }}
                                placeholder="Type to search..."
                                className="w-full px-3 py-2 border rounded"
                              />
                            </div>
                            {fuzzyFilter(availableForSelection, classQuery).map((cls, idx) => {
                              const category = getClassCategory(cls);
                              const color = getClassColor(cls, category);
                              const thumb = getClassThumbnail(cls)?.url;
                              return (
                                <button
                                  key={cls}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                                    idx === classHighlight ? "bg-gray-100" : "hover:bg-gray-50"
                                  }`}
                                  onMouseEnter={() => setClassHighlight(idx)}
                                  onClick={() => handleAddExpectedItem(cls)}
                                >
                                  <span
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                                    style={{
                                      borderColor: color,
                                      backgroundColor: colorWithOpacity(color, 0.15),
                                      color,
                                    }}
                                  >
                                    <ThumbnailPreview className={cls} thumbnailUrl={thumb}>
                                      <span className="truncate max-w-[200px]">{formatClassNameForDisplay(cls)}</span>
                                    </ThumbnailPreview>
                                  </span>
                                </button>
                              );
                            })}
                            {fuzzyFilter(availableForSelection, classQuery).length === 0 && (
                              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                No matching classes found
                              </div>
                            )}
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>

                  {/* Expected Items Table */}
                  {formData.expected_items.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">Qty</th>
                            <th className="px-3 py-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {formData.expected_items.map((item) => {
                            const category = getClassCategory(item.class_name);
                            const color = getClassColor(item.class_name, category);
                            const thumb = getClassThumbnail(item.class_name)?.url;
                            return (
                              <tr key={item.class_name} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <span
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                                    style={{
                                      borderColor: color,
                                      backgroundColor: colorWithOpacity(color, 0.15),
                                      color,
                                    }}
                                  >
                                    <ThumbnailPreview className={item.class_name} thumbnailUrl={thumb}>
                                      <span className="truncate max-w-[150px]">{formatClassNameForDisplay(item.class_name)}</span>
                                    </ThumbnailPreview>
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.expected_quantity}
                                    onChange={(e) => handleUpdateQuantity(item.class_name, parseInt(e.target.value) || 1)}
                                    className="w-full px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExpectedItem(item.class_name)}
                                    className="text-gray-400 hover:text-red-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500">
                      <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No items added yet</p>
                      <p className="text-xs mt-1">Select items from the dropdown above</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {loading ? "Creating..." : "Create Section"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CreateSectionModal;
